import { Fragment, FC, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { useDispatch } from 'react-redux';
import { setTriggerType } from '../../actions/filterActions';
import { pageTypeConstants } from '../../constants/pageTypeConstants';
import { Button } from 'dibs-elements/exports/Button';
import { Link } from 'dibs-elements/exports/Link';
import Close from 'dibs-icons/exports/Close';

import { SbSharedRefineMenuFilters } from '../SbSharedRefineMenu/SbSharedRefineMenuFilters/SbSharedRefineMenuFilters';
import { sbRespRefineMenuFiltersMap } from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { SbRespRefineMenuFilterSortFacet } from './SbRespRefineMenuFilterSortFacet';
import { SbRespRefineMenuDrawerHeader } from './SbRespRefineMenuDrawerHeader';
import { SbSharedRefineMenuDisplayPrefsSelect } from '../SbSharedRefineMenu/SbSharedRefineMenuDisplayPrefsSelect/SbSharedRefineMenuDisplayPrefsSelect';
import { SbRespRefineMenuDrawer_itemSearch$data } from './__generated__/SbRespRefineMenuDrawer_itemSearch.graphql';
import { SbRespRefineMenuDrawer_viewer$data } from './__generated__/SbRespRefineMenuDrawer_viewer.graphql';
import { SbRespRefineMenuDrawer_user$data } from './__generated__/SbRespRefineMenuDrawer_user.graphql';

import styles from './SbRespRefineMenuDrawer.scss';

function lockGlobalScroll(): void {
    document.body.classList.add(styles.lockScroll);
}

function unlockGlobalScroll(): void {
    document.body.classList.remove(styles.lockScroll);
}

type Props = {
    currency: string;
    itemSearch: SbRespRefineMenuDrawer_itemSearch$data;
    viewer: SbRespRefineMenuDrawer_viewer$data;
    user: SbRespRefineMenuDrawer_user$data;
    isClient: boolean;
    isOpen: boolean;
    closeDrawer: () => void;
};

const SbRespRefineMenuDrawerComponent: FC<Props> = ({
    currency,
    itemSearch,
    viewer,
    user,
    isClient,
    isOpen,
    closeDrawer,
}) => {
    const { filters, totalResults } = itemSearch;
    const dispatch = useDispatch();

    const escCloseDrawer = useCallback(
        e => {
            if (e.key === 'Escape') {
                closeDrawer();
            }
        },
        [closeDrawer]
    );
    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', escCloseDrawer, false);
        } else {
            document.removeEventListener('keydown', escCloseDrawer, false);
        }
        return () => {
            document.removeEventListener('keydown', escCloseDrawer, false);
        };
    }, [isOpen, escCloseDrawer]);
    useEffect(() => {
        if (isOpen) {
            dispatch(setTriggerType({ triggerType: 'drawer' }));
            lockGlobalScroll();
        } else {
            dispatch(setTriggerType({ triggerType: 'dropdown' }));
            unlockGlobalScroll();
        }
    }, [isOpen, dispatch]);

    return (
        <Fragment>
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
                className={classNames(styles.overlay, {
                    [styles.showOverlay]: isOpen,
                    [styles.hideOverlay]: !isOpen,
                })}
                onClick={closeDrawer}
                onKeyDown={closeDrawer}
                data-tn="refine-menu-drawer-overlay"
                role="navigation"
                aria-label="Facets"
            />
            <div
                className={classNames(styles.drawer, {
                    [styles.entering]: isOpen,
                    [styles.exiting]: !isOpen,
                })}
            >
                <div className={styles.container}>
                    <div className={styles.scrollArea}>
                        <div className={styles.closeButtonContainer}>
                            <Link
                                className={styles.closeButton}
                                onClick={closeDrawer}
                                dataTn="refine-menu-drawer-close"
                            >
                                <Close />
                            </Link>
                        </div>
                        <SbRespRefineMenuDrawerHeader itemSearch={itemSearch} />
                        <div className={styles.filters}>
                            <SbRespRefineMenuFilterSortFacet itemSearch={itemSearch} />
                            <SbSharedRefineMenuFilters
                                itemSearch={itemSearch}
                                isClient={isClient}
                                user={user}
                                currency={currency}
                                viewer={viewer}
                                filterMap={sbRespRefineMenuFiltersMap}
                                filters={filters}
                                initiallyExpanded={() => false}
                            />
                            {itemSearch?.pageType !== pageTypeConstants.DESIGN && (
                                <SbSharedRefineMenuDisplayPrefsSelect
                                    user={user}
                                    // @ts-ignore TODO: ts SbSharedRefineMenuDisplayPrefsSelect
                                    filters={filters}
                                    itemSearch={itemSearch}
                                    isClient={isClient}
                                />
                            )}
                        </div>
                    </div>
                    <Button onClick={closeDrawer} dataTn="refine-menu-drawer-view-results">
                        <FormattedMessage
                            id="SbRespRefineMenuDrawer.viewResults"
                            defaultMessage="VIEW {totalResults, plural, one {# RESULT} other {# RESULTS}}"
                            values={{ totalResults }}
                        />
                    </Button>
                </div>
            </div>
        </Fragment>
    );
};

export const SbRespRefineMenuDrawer = createFragmentContainer(SbRespRefineMenuDrawerComponent, {
    itemSearch: graphql`
        fragment SbRespRefineMenuDrawer_itemSearch on ItemSearchQueryConnection {
            ...SbSharedRefineMenuFilters_itemSearch
            ...SbRespSortItems_itemSearch
            ...SbSharedRefineMenuSearchWithin_itemSearch
            ...SbRespRefineMenuDrawerHeader_itemSearch
            pageType
            totalResults
            filters {
                ...SbSharedRefineMenuFilters_filters
                ...SbSharedRefineMenuDisplayPrefsSelect_filters
            }
            ...SbRespRefineMenuFilterSortFacet_itemSearch
            ...SbSharedRefineMenuDisplayPrefsSelect_itemSearch
        }
    `,
    user: graphql`
        fragment SbRespRefineMenuDrawer_user on User {
            ...SbSharedRefineMenuFilters_user
            ...SbSharedRefineMenuDisplayPrefsSelect_user
        }
    `,
    viewer: graphql`
        fragment SbRespRefineMenuDrawer_viewer on Viewer {
            ...SbSharedRefineMenuFilters_viewer
        }
    `,
});
